import { inject } from '@angular/core';
import { CanActivateFn, CanDeactivateFn, Router } from '@angular/router';
import { hasProjectContentPermission } from '@core/models';
import { ContextStateService } from '@core/services/context-state.service';
import { ModuleRouteContextService } from '@core/services/modules/module-route-context.service';
import { SnackbarService } from '@core/services/snackbar';

/**
 * Validates the `:module` slug for system-wide admin settings (`/admin/modules/:module/...`).
 * Unlike {@link moduleContextGuard}, it must not touch `ContextStateService`'s selected module/organization
 * state — that state is scoped to the signed-in admin's own organization and is shared with the global
 * sidenav/header, which are unrelated to which module type's platform-wide defaults are being edited here.
 */
export const systemModuleSlugGuard: CanActivateFn = route => {
    const moduleRouteContextService = inject(ModuleRouteContextService);
    const router = inject(Router);
    const moduleSlug = route.paramMap.get('module');

    if (moduleRouteContextService.getModuleTypeFromSlug(moduleSlug) === null) {
        return router.createUrlTree(['/admin/modules']);
    }

    return true;
};

export const moduleContextGuard: CanActivateFn = route => {
    const contextStateService = inject(ContextStateService);
    const router = inject(Router);
    const moduleSlug = route.paramMap.get('module');

    if (!contextStateService.setSelectedModuleBySlug(moduleSlug)) {
        return router.createUrlTree(['/']);
    }

    contextStateService.setSelectedProject(null);

    return true;
};

export const projectContextGuard: CanActivateFn = route => {
    const contextStateService = inject(ContextStateService);
    const router = inject(Router);
    const snackbarService = inject(SnackbarService);
    const moduleSlug = route.parent?.paramMap.get('module');
    const projectUrlSafeName = route.paramMap.get('project');

    if (!contextStateService.setSelectedModuleBySlug(moduleSlug)) {
        return router.createUrlTree(['/']);
    }

    if (!contextStateService.setSelectedProjectByUrlSafeName(projectUrlSafeName)) {
        return router.createUrlTree(['/', moduleSlug]);
    }

    if (!hasProjectContentPermission(contextStateService.currentUserCurrentProjectPermissions())) {
        contextStateService.setSelectedProject(null);
        snackbarService.open('str_contextSwitcher_projectAccessDenied', 'warning');
        return router.createUrlTree(['/', moduleSlug, 'home']);
    }

    return true;
};

export const projectContextDeactivateGuard: CanDeactivateFn<unknown> = () => {
    inject(ContextStateService).setSelectedProject(null);
    return true;
};
